/**
 * AutoCompleteField component
 * A React Select Wrapper
 * Use prop "creatable" to allow selection of options that don't exist in the list
 * Styles overriding using this documentation guide https://react-select.com/styles
 */

import React from "react"
import Async from "react-select/async"
import PropTypes from "prop-types"
import {
  SingleValueContainer,
  FormIcon,
  ErrorMessage,
  HighlighterWrapper,
  Hint,
  Label,
  ControlWrapper,
  customStylesStandard,
  errorStyles,
  customStylesSide,
} from "./styles"
import AsyncCreatableSelect from "react-select/async-creatable"
import { components } from "react-select"
import Text from "components/Text"
import Icon from "components/Icon"
import { MIN_AMOUNT_TO_TYPE, DEBOUNCE_DELAY } from "utils/constants"
import { getQueryParameters } from "utils/utils"
import Translate from "components/Translate/container"

class AutoCompleteField extends React.Component {
  constructor(props) {
    super(props)

    this.debounceTimeout = undefined

    this.state = {
      touched: false,
      inputValue: null,
      focused: false,
      lastEntered: Date.now(),
      delay: DEBOUNCE_DELAY,
    }
  }

  componentDidMount() {
    let query = {}
    if (window.location.search) {
      query = getQueryParameters(window.location)
    }
    if (query.delay) {
      this.setState({
        delay: parseInt(query.delay),
      })
    }
  }

  componentWillUpdate(nextProps) {
    if (this.props.value && (!nextProps.value || nextProps.value === "")) {
      this.setState({ touched: false })
    }
  }

  handleChange = option => {
    const { id, onChange } = this.props
    if (onChange) onChange(id, option)
  }

  handleInputChange = inputValue => {
    this.setState({
      inputValue: inputValue,
    })
  }

  makeCall = (inputValue, resolve) => {
    this.setState({
      loading: true,
      lastEntered: Date.now(),
    })
    return this.props.loadOptions(inputValue).then(results => {
      this.setState({
        inputValue: false,
        loading: false,
        results,
      })
      resolve(results)
    })
  }

  /**
   * On each change, if there is already a timeout set
   * to do the lookup, we clear it and then set
   * a new timeout to do the lookup if the minimum amount
   * of characters have been entered. Once the delay
   * has elapsed, the debounced function executes and we
   * finally do the lookup. This way we're only sending
   * one data load request when the user has finished
   * typing, rather than with each change of the input.
   *
   * @param {*} inputValue
   * @returns Promise to make the data lookup call
   */
  promiseOptions = inputValue => {
    if (this.debounceTimeout) {
      window.clearTimeout(this.debounceTimeout)
      this.debounceTimeout = undefined
    }

    return new Promise((resolve, reject) => {
      if (inputValue.length > this.props.minAmount) {
        this.debounceTimeout = window.setTimeout(() => {
          this.makeCall(inputValue, resolve).catch(err => {
            reject(err)
          })
          this.debounceTimeout = undefined
        }, DEBOUNCE_DELAY)
      } else {
        reject()
      }
    })
  }

  render() {
    const {
      value,
      medium,
      width,
      label,
      hint,
      id,
      error,
      errorMessage,
      isDisabled,
      creatable,
      forceErrorDisplay,
      isMulti,
      disableShowValue,
      placeholderMarginLeft,
      translate = true,
      placeholder,
    } = this.props

    const { focused, inputValue } = this.state

    const customStyles = isMulti ? customStylesSide : customStylesStandard

    const Component = creatable ? AsyncCreatableSelect : Async

    const SingleValue = ({ children, ...props }) => (
      <components.SingleValue {...props} style={{ fontWeight: "bold" }}>
        <SingleValueContainer>{children}</SingleValueContainer>
      </components.SingleValue>
    )

    const localMessages = {
      placeholder: <Translate>{placeholder || "Please select"}</Translate>,
      noOptionsMessage: () => <Translate>No options</Translate>,
      loadingMessage: () => <Translate>Loading...</Translate>,
    }

    return (
      <div className={error && forceErrorDisplay ? "hasError" : null}>
        {label ? (
          <Label as={"label"} htmlFor={id}>
            {translate ? <Translate>{label}</Translate> : label}
          </Label>
        ) : null}
        {hint ? (
          <Hint as={"p"} variant={"hint"}>
            {translate ? <Translate>{hint}</Translate> : hint}
          </Hint>
        ) : null}
        <ControlWrapper medium={medium} width={width}>
          <Component
            {...this.props}
            loadOptions={this.promiseOptions}
            components={{ SingleValue }}
            isDisabled={isDisabled}
            medium={medium}
            placeholderMarginLeft={placeholderMarginLeft}
            value={
              (focused && !inputValue) || disableShowValue ? "null" : value
            }
            onChange={this.handleChange}
            onInputChange={this.handleInputChange}
            onFocus={() => {
              this.setState({ focused: true })
            }}
            onBlur={ev => {
              const { onCreateOption } = this.props
              if (onCreateOption && ev.target.value) {
                ev.preventDefault()
                onCreateOption(ev.target.value)
              }
              this.setState({ focused: false })
            }}
            onMenuClose={() => {
              this.setState({ focused: false })
            }}
            formatOptionLabel={option => {
              return (
                <HighlighterWrapper
                  autoEscape
                  searchWords={[this.state.inputValue]}
                  textToHighlight={option.value || ""}
                />
              )
            }}
            styles={
              error && forceErrorDisplay
                ? { ...customStyles, ...errorStyles }
                : customStyles
            }
            {...localMessages}
          />
          {forceErrorDisplay && error && errorMessage ? (
            <ErrorMessage medium={medium}>
              <Text variant={"errorInputHelp"} id={`error-${id}`}>
                {translate ? (
                  <Translate>{errorMessage}</Translate>
                ) : (
                  errorMessage
                )}
              </Text>
              <FormIcon variant={"danger"}>
                <Icon variant={"warning"} />
              </FormIcon>
            </ErrorMessage>
          ) : null}
        </ControlWrapper>
      </div>
    )
  }
}

AutoCompleteField.propTypes = {
  creatable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  errorMessage: PropTypes.node,
  label: PropTypes.node,
  medium: PropTypes.bool,
  forceErrorDisplay: PropTypes.bool,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  placeholderMarginLeft: PropTypes.string,
}

AutoCompleteField.defaultProps = {
  creatable: false,
  isDisabled: false,
  minAmount: MIN_AMOUNT_TO_TYPE,
}

export default AutoCompleteField
